import {Link, useNavigate, useParams} from "react-router-dom";
import {useAlert, useAuth, useDarkMode} from "../../../../context";
import {ColumnizeText, FigureText, OurClearAlert, PageTitle, SingleText} from "../../../_components";
import {Button, Card, Figure, ListGroup, Stack} from "react-bootstrap";
import {isoTimestampToReadableDate} from "../utilities/readableDate";
import {ButtonDelete} from "../../../_components/DeleteButton";
import React, {useMemo, useState} from "react";
import {Formik, Form as FormikForm, FieldArray} from "formik";
import {useTagCount, useTagList} from "../tag/useTags";
import {useInfiniteScroll, useMediaCount, useMediaDetails, useMediaList, useProductCount, useProductDetails, useProductList} from "../../../../hooks";
import {DynamicBox, DynamicDialog} from "../../../_components/BoxGroupLayout";
import {useTip} from "./useTip";
import {config} from "../../../../utils2";
import {WhiteCard} from "../../../device/_components/WhiteCard";


export function AddEditTip() {
    const {tiid} = useParams()
    const {userId} = useAuth()
    const {success} = useAlert()
    const navigate = useNavigate()

    const {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        errorValue,
        clearErrorValue,
    } = useTip(userId, tiid)

    const edit = tiid !== "add"

    function handleSubmit(values, actions) {
        if (edit) {
            editItem(values)
                .then(msg => {
                    success(msg)
                    navigate(-1)
                })
                .catch(console.warn)
                .finally(() => actions.setSubmitting(false))
        } else {
            addItem(values)
                .then(msg => {
                    success(msg)
                    navigate(-1)
                })
                .catch(console.warn)
                .finally(() => actions.setSubmitting(false))
        }
    }


    function handleOnDelete() {
        deleteItem()
            .then(msg => {
                success(msg)
                navigate(-1)
            })
            .catch(console.warn)
    }

    return <>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>

        <PageTitle title="Tip Details">{edit ? "Edit" : "Add"} tip.</PageTitle>

        <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={value} enableReinitialize={true}>
            {({isSubmitting, resetForm}) => (
                <FormikForm>
                    <WhiteCard>
                        <ColumnizeText>
                            <SingleText name="title" label="Title"/>
                            <SingleText name="url" label="External URL" tip="For GitHub resources: /git/github-folder/filename.md"/>
                        </ColumnizeText>

                        <SingleText name="description" label="Description" type="textarea" maxLength={300}/>
                    </WhiteCard>

                    <WhiteCard>
                        <FieldArray name="tags" component={LinkedTags}/>
                    </WhiteCard>

                    <WhiteCard>
                        <FieldArray name="medias" component={LinkedMedias}/>
                    </WhiteCard>

                    <WhiteCard>
                        <FieldArray name="products" component={LinkedProducts}/>
                    </WhiteCard>

                    <Stack className="my-2 text-secondary">
                        {value.created_at && <span>Created at: {isoTimestampToReadableDate(value.created_at)}</span>}
                        {value.updated_at && <span>Updated at: {isoTimestampToReadableDate(value.updated_at)}</span>}
                    </Stack>


                    <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                        <Button as={Link} variant="link" to={-1}>Go back</Button>
                        <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                        {edit && <ButtonDelete variant="danger" onDelete={handleOnDelete} value={value.name}>Delete</ButtonDelete>}
                        <Button type="submit" variant="success" disabled={isSubmitting}>Save</Button>
                    </Stack>
                </FormikForm>
            )}
        </Formik>
    </>
}

function LinkedTags({push, remove, form}) {
    const [show, setShow] = useState(false)
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useTagCount()
    const {list, loadingList} = useTagList(pageSize, pageIndex, null, "name_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.tags.find(item2 => item2.id === item.id)), [list, form.values.tags])
    const renderList = useMemo(() => tempList.filter(item => item.name.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])
    const selectedItems = useMemo(() => form.values.tags.sort((a, b) => a.name.localeCompare(b.name)), [form.values.tags])

    const isInvalid = typeof form.errors.tags === "string"


    return (
        <>
            <DynamicBox label="Link tags" isInvalid={isInvalid} feedback={form.errors.tags} button="Tag"
                        onClick={() => setShow(true)}>
                {selectedItems.map(item => (
                    <Button key={item.id} className="mb-2" onClick={() => remove(item.id)} variant={item.variant}>{item.name} <i
                        className="bi bi-x-lg"/></Button>
                ))}
            </DynamicBox>
            <DynamicDialog title="Select Tag" show={show} onHide={() => setShow(false)} filter={filter} setFilter={setFilter}>
                <div>Loaded {list.length - selectedItems.length} of {count - selectedItems.length}</div>
                {renderList && renderList.length > 0 ?
                    renderList.map((item, index) => (
                        <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                            <Button variant={item.variant} onClick={() => push(list.find(c => c.id === item.id))} className="mb-2">{item.name}</Button>
                        </ListGroup.Item>
                    ))
                    :
                    <span className="text-center">No tag found</span>
                }
            </DynamicDialog>
        </>
    )
}

function LinkedMedias({move, push, remove, form}) {
    const [show, setShow] = useState(false)

    const isInvalid = typeof form.errors.medias === "string"

    return (<>
        <DynamicBox label="Link medias" isInvalid={isInvalid} feedback={form.errors.medias} button="Media" className="mt-3" onClick={() => setShow(true)}>
            {form.values.medias.map((item, idx) => (
                <LinkedMediasCard key={idx} idx={idx} item={item} move={move} remove={remove} idxMax={form.values.medias.length - 1}/>
            ))}
        </DynamicBox>
        <LinkedMediasDialog show={show} onHide={() => setShow(false)} push={push} form={form}/>
    </>)
}

function LinkedMediasCard({item, idx, move, remove, idxMax}) {
    const {value} = useMediaDetails(item?.id)
    const {darkMode} = useDarkMode()

    return <Card key={idx} className="position-relative mb-1" style={{ width: '15rem' }}>
        <div className="text-center">
            <Figure.Image
                className=""
                src={config.get_public_media(value.path, "thumb", darkMode)}
                alt={item.description}
                height="120px"
                width="120px"
                loading="lazy"
            />
        </div>
        <Card.Body className="">
            <Card.Text className="text-center"><Link to={`/backend/media/${value.id}`}
                                                     className="link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                {value.description}
            </Link></Card.Text>
        </Card.Body>
        <Card.Footer>
            <Stack direction="horizontal" className="justify-content-between" gap={1}>
                <Button variant="outline-secondary" className="bi bi-arrow-left" size="sm" onClick={() => move(idx, idx - 1)} disabled={idx === 0}></Button>
                <Button variant="outline-danger" className="bi bi-trash" size="sm" onClick={() => remove(idx)}></Button>
                <Button variant="outline-secondary" className="bi bi-arrow-right" size="sm" onClick={() => move(idx, idx + 1)} disabled={idx === idxMax}></Button>
            </Stack>
        </Card.Footer>
    </Card>
}

function LinkedMediasDialog({show, onHide, push, form}) {
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useMediaCount()
    const {list, loadingList} = useMediaList(pageSize, pageIndex, null, "desc_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.medias.find(item2 => item2.id === item.id)), [list, form.values.medias])
    const renderList = useMemo(() => tempList.filter(item => item.description.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])

    return <DynamicDialog title="Select Media" show={show} onHide={onHide} filter={filter} setFilter={setFilter}>
        <div>Loaded {list.length - form.values.medias.length} of {count - form.values.medias.length}</div>
        {renderList && renderList.length > 0 ?
            renderList.map((item, index) => <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                <FigureText path={list.find(media => media.id === item.id)?.path} alt={item.description}
                            onClick={() => push(item)}>
                    <div>
                        <h5>{item.description}</h5>
                        <span>{item.type}</span>
                    </div>
                </FigureText>
            </ListGroup.Item>)
            :
            <span className="text-center">No image found</span>
        }
    </DynamicDialog>
}

function LinkedProducts({move, remove, push, form}) {

    const name = "products"
    const [show, setShow] = useState(false)

    return <>
        <DynamicBox className="mt-3" label="Link products" isInvalid={typeof form.errors[name] === "string"} feedback={form.errors[name]} button="Product"
                    onClick={() => setShow(true)}>
            {form.values[name].map((item, idx) => <LinkedProductCard key={idx} idx={idx} item={item} move={move} remove={remove} idxMax={form.values[name].length - 1}/>)}
        </DynamicBox>
        <LinkedProductsDialog show={show} onHide={() => setShow(false)} push={push} selected={form.values[name]}/>
    </>
}

function LinkedProductCard({item, idx, move, remove, idxMax}) {

    const {value, loadingValue, errorValue, clearError} = useProductDetails(item?.id)
    const {darkMode} = useDarkMode()

    return <>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearError}/>

        {loadingValue ?
            <div>Loading...</div>
            :
            <Card className="mb-1" style={{ width: '25rem' }}>
                {/*<Card.Img variant="top" src={config.get_public_media(value?.medias[0]?.path, "thumb", darkMode)}/>*/}
                <div className="text-center">
                    <Figure.Image
                        className=""
                        src={config.get_public_media(value?.medias[0]?.path, "thumb", darkMode)}
                        alt={item.description}
                        height="120px"
                        width="120px"
                        loading="lazy"
                    />
                </div>
                <Card.Body className="">
                    <Card.Title>{value.title}</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">{value.SKU}</Card.Subtitle>
                    <Card.Text>{value.summary}</Card.Text>
                </Card.Body>
                <Card.Footer>
                    <Stack direction="horizontal" className="justify-content-between" gap={1}>
                        <Button variant="outline-secondary" className="bi bi-arrow-left" size="sm" onClick={() => move(idx, idx - 1)} disabled={idx === 0}></Button>
                        <Button variant="outline-danger" className="bi bi-trash" size="sm" onClick={() => remove(idx)}></Button>
                        <Button variant="outline-secondary" className="bi bi-arrow-right" size="sm" onClick={() => move(idx, idx + 1)} disabled={idx === idxMax}></Button>
                    </Stack>
                </Card.Footer>
            </Card>
        }
    </>
}

function LinkedProductsDialog({show, onHide, push, selected}) {
    const {pid} = useParams()
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useProductCount()
    const {list, loadingList} = useProductList(pageSize, pageIndex, null, "title_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => item.id !== pid && !selected.find(item2 => item2.id === item.id)), [list, pid, selected])
    const renderList = useMemo(() => tempList.filter(item => item.title.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])

    const prodLoaded = renderList.length
    const prodTotal = count - (pid === "add" ? 0 : 1) - selected.length

    return <DynamicDialog title="Select Related Products" show={show} onHide={onHide} filter={filter} setFilter={setFilter}>
        <div>Loaded {prodLoaded} of {prodTotal}</div>
        {renderList && renderList.length > 0 ?
            renderList.map((item, index) =>
                <FigureText key={item.id} ref={renderList.length === index + 1 ? lastElementRef : null} alt={item.id}
                            path={item.medias[0]?.path}
                            onClick={() => push(item)}>
                    <div>
                        <h5>{item.title}</h5>
                        <span className="fw-light">{item.SKU}</span>
                    </div>
                </FigureText>)
            :
            <span className="text-center">No product found</span>
        }
    </DynamicDialog>
}